import { getSupabaseClient } from '@/integrations/supabase/client';
import { Product } from '@/types';
import { promotionsService } from '@/services/promotions/promotionsService';

export const productsService = {

  async getProducts(organizationId: string) {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('products')
      .select(`
        *,
        providers (
          id,
          name,
          phone,
          bank_account_number,
          bank,
          email,
          organization_id,
          created_at,
          updated_at
        ),
        product_categories (
          id,
          name,
          show_in_sales,
          organization_id,
          print_in_kitchen,
          print_in_bar,
          created_at,
          updated_at,
          cabys_code,
          cabys_tax,
          activity_code
        )
      `)
      .eq('organization_id', organizationId)
      .order('name');

    if (error) throw error;

    return data.map(product => ({
      id: product.id,
      name: product.name,
      // Prefer joined category name (stays in sync when category is renamed)
      category: product.product_categories?.name,
      categoryId: product.category_id,
      cost: Number(product.cost || 0),
      price: Number(product.price),
      stock: Number(product.stock || 0),
      minimumStockLevel: product.minimum_stock_level ? Number(product.minimum_stock_level) : undefined,
      organizationId: product.organization_id,
      providerId: product.provider_id,
      providerCode: product.provider_code,
      notes: product.notes,
      avoidTracking: product.avoid_tracking || false,
      isEnabled: product.is_enabled ?? true,
      availableWeekdays: product.available_weekdays,
      isPinned: product.is_pinned ?? false,
      isComposite: product.is_composite ?? false,
      isSoldByWeight: product.is_sold_by_weight ?? false,
      provider: product.providers ? {
        id: product.providers.id,
        name: product.providers.name,
        phone: product.providers.phone,
        bankAccountNumber: product.providers.bank_account_number,
        bank: product.providers.bank,
        email: product.providers.email,
        organizationId: product.providers.organization_id,
        createdAt: new Date(product.providers.created_at),
        updatedAt: new Date(product.providers.updated_at)
      } : undefined,
      productCategory: product.product_categories ? {
        id: product.product_categories.id,
        name: product.product_categories.name,
        printInKitchen: product.product_categories.print_in_kitchen,
        printInBar: product.product_categories.print_in_bar,
        showInSales: product.product_categories.show_in_sales,
        organizationId: product.product_categories.organization_id,
        createdAt: new Date(product.product_categories.created_at),
        updatedAt: new Date(product.product_categories.updated_at),
        cabysCode: product.product_categories.cabys_code || undefined,
        cabysTax: product.product_categories.cabys_tax || undefined,
        activityCode: product.product_categories.activity_code || undefined
      } : undefined
    })) as Product[];
  },

  async getProductsForSales(organizationId: string) {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('products')
      .select(`
        *,
        providers (
          id,
          name,
          phone,
          bank_account_number,
          bank,
          email,
          organization_id,
          created_at,
          updated_at
        ),
        product_categories (
          id,
          name,
          show_in_sales,
          organization_id,
          print_in_kitchen,
          print_in_bar,
          created_at,
          updated_at,
          cabys_code,
          cabys_tax,
          activity_code 
        )
      `)
      .eq('organization_id', organizationId)
      .order('name');

    if (error) throw error;

    const filteredData = data.filter(product => {
      if (!product.product_categories) return true;
      return product.product_categories.show_in_sales === true;
    });

    return filteredData.map(product => ({
      id: product.id,
      name: product.name,
      // Prefer joined category name (stays in sync when category is renamed)
      category: product.product_categories?.name,
      categoryId: product.category_id,
      cost: Number(product.cost || 0),
      price: Number(product.price),
      stock: Number(product.stock || 0),
      minimumStockLevel: product.minimum_stock_level ? Number(product.minimum_stock_level) : undefined,
      organizationId: product.organization_id,
      providerId: product.provider_id,
      providerCode: product.provider_code,
      isEnabled: product.is_enabled ?? true,
      isPinned: product.is_pinned ?? false,
      isComposite: product.is_composite ?? false,
      isSoldByWeight: product.is_sold_by_weight ?? false,
      notes: product.notes,
      avoidTracking: product.avoid_tracking || false,
      availableWeekdays: product.available_weekdays,
      provider: product.providers ? {
        id: product.providers.id,
        name: product.providers.name,
        phone: product.providers.phone,
        bankAccountNumber: product.providers.bank_account_number,
        bank: product.providers.bank,
        email: product.providers.email,
        organizationId: product.providers.organization_id,
        createdAt: new Date(product.providers.created_at),
        updatedAt: new Date(product.providers.updated_at)
      } : undefined,
      productCategory: product.product_categories ? {
        id: product.product_categories.id,
        name: product.product_categories.name,
        printInKitchen: product.product_categories.print_in_kitchen,
        printInBar: product.product_categories.print_in_bar,
        showInSales: product.product_categories.show_in_sales,
        organizationId: product.product_categories.organization_id,
        createdAt: new Date(product.product_categories.created_at),
        updatedAt: new Date(product.product_categories.updated_at),
        cabysCode: product.product_categories.cabys_code,
        cabysTax: product.product_categories.cabys_tax,
        activityCode: product.product_categories.activity_code
      } : undefined
    })) as Product[];
  },

  async createProduct(product: Omit<Product, 'id' | 'organizationId' | 'provider' | 'productCategory'>, organizationId: string) {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('products')
      .insert([{
        name: product.name,
        category: product.category,
        category_id: product.categoryId,
        cost: product.cost || null,
        price: product.price,
        stock: product.stock || 0,
        minimum_stock_level: product.minimumStockLevel || null,
        provider_id: product.providerId || null,
        provider_code: product.providerCode || null,
        notes: product.notes || null,
        organization_id: organizationId,
        avoid_tracking: product.avoidTracking || false,
        is_enabled: product.isEnabled ?? true,
        available_weekdays: product.availableWeekdays ?? null,
        is_pinned: product.isPinned ?? false,
        is_composite: product.isComposite ?? false,
        is_sold_by_weight: product.isSoldByWeight ?? false
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateProduct(product: Product) {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('products')
      .update({
        name: product.name,
        category: product.category,
        category_id: product.categoryId,
        cost: product.cost || null,
        price: product.price,
        stock: product.stock,
        minimum_stock_level: product.minimumStockLevel || null,
        provider_id: product.providerId || null,
        provider_code: product.providerCode || null,
        notes: product.notes || null,
        updated_at: new Date().toISOString(),
        is_enabled: product.isEnabled ?? true,
        avoid_tracking: product.avoidTracking || false,
        available_weekdays: product.availableWeekdays ?? null,
        is_pinned: product.isPinned ?? false,
        is_composite: product.isComposite ?? false,
        is_sold_by_weight: product.isSoldByWeight ?? false
      })
      .eq('id', product.id)
      .select()
      .single();

    if (error) throw error;

    if (product.organizationId) {
      await promotionsService.syncPromotionActiveFromProduct(
        product.organizationId,
        product.id,
        product.isEnabled !== false
      );
    }

    return data;
  },

  async deleteProduct(productId: string) {
    const supabase = getSupabaseClient();
    const { error } = await supabase
      .from('products')
      .delete()
      .eq('id', productId);

    if (error) throw error;
  },

  async getProductStock(productId: string) {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('products')
      .select('stock')
      .eq('id', productId)
      .single();

    if (error) throw error;
    return Number(data.stock);
  },

  async updateProductStock(productId: string, newStock: number) {
    const supabase = getSupabaseClient();
    const { error } = await supabase
      .from('products')
      .update({
        stock: newStock >= 0 ? newStock : 0,
        updated_at: new Date().toISOString()
      })
      .eq('id', productId);

    if (error) throw error;
  },

  async updateProductStockByDelta(productId: string, deltaQuantity: number) {
    const currentStock = await this.getProductStock(productId);
    await this.updateProductStock(productId, currentStock + deltaQuantity);
    return currentStock + deltaQuantity;
  },

  async togglePinProduct(productId: string, isPinned: boolean) {
    const supabase = getSupabaseClient();
    const { error } = await supabase
      .from('products')
      .update({ is_pinned: isPinned, updated_at: new Date().toISOString() })
      .eq('id', productId);

    if (error) throw error;
  }
};
